import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const WS_KEY = "bridgehub_workspace_id";

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const prevUserIdRef = useRef(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspaceId, setCurrentWorkspaceIdState] = useState(
    () => localStorage.getItem(WS_KEY) || null
  );

  const setCurrentWorkspaceId = (id) => {
    setCurrentWorkspaceIdState(id);
    if (id) {
      localStorage.setItem(WS_KEY, id);
      axios.defaults.headers.common["X-Workspace-Id"] = id;
    } else {
      localStorage.removeItem(WS_KEY);
      delete axios.defaults.headers.common["X-Workspace-Id"];
    }
  };

  // Clear stale workspace when a different user logs in
  useEffect(() => {
    const currentUserId = user?.id || null;
    if (prevUserIdRef.current && currentUserId && prevUserIdRef.current !== currentUserId) {
      localStorage.removeItem(WS_KEY);
      setCurrentWorkspaceIdState(null);
      delete axios.defaults.headers.common["X-Workspace-Id"];
    }
    prevUserIdRef.current = currentUserId;
  }, [user?.id]);

  const fetchWorkspaces = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/api/workspaces`);
      setWorkspaces(data);
      if (data.length > 0) {
        const saved = localStorage.getItem(WS_KEY);
        // Only reuse saved workspace_id if the current user is its owner or a member
        const valid = saved && data.find(w =>
          w.id === saved && (
            w.owner_id === user.id ||
            (w.members || []).some(m => m.user_id === user.id)
          )
        );
        const target = valid ? saved : data[0].id;
        setCurrentWorkspaceId(target);
      }
    } catch {}
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchWorkspaces();
    } else {
      setWorkspaces([]);
    }
  }, [user, fetchWorkspaces]);

  useEffect(() => {
    // Always sync axios header from state (covers initial load from localStorage)
    if (currentWorkspaceId) {
      axios.defaults.headers.common["X-Workspace-Id"] = currentWorkspaceId;
    } else {
      delete axios.defaults.headers.common["X-Workspace-Id"];
    }
  }, [currentWorkspaceId]);

  const currentWorkspace = workspaces.find(w => w.id === currentWorkspaceId) || workspaces[0] || null;

  // Derive RBAC helpers from the current workspace's my_role field
  const ROLE_LEVELS = {
    super_admin: 6, workspace_admin: 5, owner: 5, demo_admin: 4,
    admin: 4, developer: 3, member: 2, viewer: 1,
  };
  const myRole = currentWorkspace?.my_role || "viewer";
  // Prefer backend-computed level (avoids legacy role string mismatch); superadmin always gets 6
  const myRoleLevel = user?.is_superadmin
    ? 6
    : (currentWorkspace?.my_role_level ?? ROLE_LEVELS[myRole] ?? 1);
  const canWrite = myRoleLevel >= 3;     // developer+
  const canAdmin = myRoleLevel >= 5;     // workspace_admin+

  // ── Trial state ───────────────────────────────────────────────────────────
  const trialExpiresAt = currentWorkspace?.trial_expires_at
    ? new Date(currentWorkspace.trial_expires_at) : null;
  const isTrialActive  = trialExpiresAt !== null && trialExpiresAt > new Date();
  const isTrialExpired = trialExpiresAt !== null && trialExpiresAt <= new Date();
  const trialDaysLeft  = isTrialActive
    ? Math.ceil((trialExpiresAt - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <WorkspaceContext.Provider value={{
      workspaces,
      currentWorkspaceId,
      currentWorkspace,
      setCurrentWorkspaceId,
      refreshWorkspaces: fetchWorkspaces,
      myRole,
      myRoleLevel,
      canWrite,
      canAdmin,
      isTrialActive,
      isTrialExpired,
      trialDaysLeft,
      trialExpiresAt,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  return useContext(WorkspaceContext);
}
